import React from "react";
import { Wrench, Phone, MapPin, Mail, Facebook, Instagram, Star, Flag } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const year = new Date().getFullYear();

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0B132B] text-white border-t border-white/10"
    >
      <div className="stripe-red" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-[#D32F2F] flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-lg">VETERAN'S AUTO</div>
                <div className="font-mono-label text-[#D32F2F]">
                  Repair Center · Madison Heights
                </div>
              </div>
            </div>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-md">
              A veteran-owned full-service auto repair shop proudly serving
              Madison Heights and all of Metro Detroit with honest,
              high-integrity automotive care.
            </p>
            <div className="mt-5 flex items-center gap-4">
              <span className="inline-flex items-center gap-2 px-3 py-1 border border-[#D32F2F]/40 text-[#D32F2F] font-mono-label">
                <Flag className="w-3 h-3" strokeWidth={2.5} /> Veteran-Owned
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 border border-white/20 text-white/80 font-mono-label">
                <Star className="w-3 h-3 fill-[#D32F2F] text-[#D32F2F]" /> 5.0 Local Rating
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div className="font-mono-label text-[#D32F2F] mb-4">Navigate</div>
            <ul className="space-y-2 text-sm">
              {[
                ["Home", "#home"],
                ["Services", "#services"],
                ["Reviews", "#reviews"],
                ["About", "#about"],
                ["FAQ", "#faq"],
                ["Contact", "#contact"],
              ].map(([label, href]) => (
                <li key={href}>
                  <a
                    href={href}
                    data-testid={`footer-link-${label.toLowerCase()}`}
                    className="text-white/80 hover:text-[#D32F2F] transition-colors"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="font-mono-label text-[#D32F2F] mb-4">Visit &amp; Call</div>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3 items-start">
                <MapPin className="w-4 h-4 text-[#D32F2F] mt-0.5 shrink-0" strokeWidth={2.5} />
                <a
                  href={BUSINESS.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white"
                >
                  {BUSINESS.address1}
                  <br />
                  {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="w-4 h-4 text-[#D32F2F] shrink-0" strokeWidth={2.5} />
                <a
                  href={`tel:${BUSINESS.phoneDial}`}
                  data-testid="footer-phone-link"
                  className="text-white/80 hover:text-white"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="w-4 h-4 text-[#D32F2F] shrink-0" strokeWidth={2.5} />
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="text-white/80 hover:text-white"
                >
                  {BUSINESS.email}
                </a>
              </li>
            </ul>

            <div className="mt-5 flex gap-3">
              <a
                href="#"
                data-testid="footer-facebook"
                aria-label="Facebook"
                className="w-10 h-10 border border-white/20 hover:bg-[#D32F2F] hover:border-[#D32F2F] flex items-center justify-center transition-colors"
              >
                <Facebook className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href="#"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="w-10 h-10 border border-white/20 hover:bg-[#D32F2F] hover:border-[#D32F2F] flex items-center justify-center transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </div>

        {/* Service areas */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="font-mono-label text-[#D32F2F] mb-3">Service Areas</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/70">
            {BUSINESS.serviceAreas.map((a) => (
              <span key={a}>{a}</span>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/60">
          <div>
            © {year} {BUSINESS.legal}. All rights reserved.
          </div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Terms of Service</a>
            <a href="#" className="hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
